import { useLocation, useNavigate } from "react-router-dom";
import routes from "routes.js";
import SignInForm from "./components/SignInForm";
import RegisterForm from "./components/RegisterForm";
import ForgotPasswordForm from "./components/ForgotPasswordForm";

// Which form belongs to which URL. Panel order comes from routes.js so the
// paths have a single source of truth; this only supplies the form for each.
const FORMS = {
  login: ({ navigate, username }) => (
    <SignInForm
      initialUsername={username}
      onSwitchToRegister={() => navigate("/register")}
      onSwitchToForgot={() => navigate("/forgot-password")}
    />
  ),
  register: ({ navigate }) => (
    <RegisterForm
      onSwitchToSignIn={(name) =>
        navigate("/login", { replace: true, state: name ? { username: name } : undefined })
      }
    />
  ),
  "forgot-password": ({ navigate }) => (
    <ForgotPasswordForm onSwitchToSignIn={() => navigate("/login")} />
  ),
};

const panels = routes.filter((route) => route.area === "auth" && route.carousel);

if (process.env.NODE_ENV !== "production") {
  const missing = panels.filter((p) => !FORMS[p.path]).map((p) => p.path);
  if (missing.length) {
    console.warn(`AuthCarousel: no form registered for ${missing.join(", ")}`);
  }
}

// A layout route, so it stays mounted while the child path changes. That is what
// makes the slide possible: all panels remain rendered, and only the transform
// changes. The URL drives the position, so deep links and the back button work,
// which the previous local-state version could not do.
export default function AuthCarousel() {
  const location = useLocation();
  const navigate = useNavigate();

  const active = Math.max(0, panels.findIndex((p) => location.pathname === `/${p.path}`));
  const width = `${panels.length * 100}%`;
  const offset = `translateX(-${active * (100 / panels.length)}%)`;

  return (
    <div className="flex w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:justify-start">
      <div className="w-full max-w-full md:pl-4 lg:pl-0 xl:max-w-[420px]">
        <div className="w-full overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-in-out motion-reduce:transition-none"
            style={{ width, transform: offset }}
            data-testid="auth-carousel-track"
          >
            {panels.map((panel, i) => {
              const render = FORMS[panel.path];
              const isActive = i === active;
              return (
                <div
                  key={panel.path}
                  data-testid={`auth-panel-${panel.path}`}
                  className="flex-shrink-0"
                  style={{ width: `${100 / panels.length}%` }}
                  // Off-screen panels stay in the DOM for the animation, so they
                  // must be removed from the tab order and the accessibility
                  // tree. The old carousel left all three focusable.
                  inert={!isActive}
                  aria-hidden={!isActive}
                >
                  {render ? render({ navigate, username: location.state?.username ?? "" }) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
